import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Clock } from 'lucide-react';
import Lenis from '@studio-freight/lenis';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function FormPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    social: '',
    location: '',
    helpNeeded: [],
    budget: '',
    frustration: '',
    desiredResult: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    window.scrollTo(0, 0);

    // Track page visit
    fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'page_view' }),
    }).catch(() => {});

    return () => lenis.destroy();
  }, []);

  const totalSteps = 9;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (option) => {
    setFormData(prev => {
      const current = prev.helpNeeded;
      if (current.includes(option)) {
        return { ...prev, helpNeeded: current.filter(o => o !== option) };
      }
      return { ...prev, helpNeeded: [...current, option] };
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await fetch(`${BACKEND_URL}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      setIsComplete(true);
    } catch (error) {
      console.error('Submission error:', error);
    }
    setIsSubmitting(false);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.name.trim().length > 0;
      case 1: return formData.business.trim().length > 0;
      case 2: return formData.social.trim().length > 0;
      case 3: return formData.location.trim().length > 0;
      case 4: return formData.helpNeeded.length > 0;
      case 5: return formData.budget.length > 0;
      case 6: return formData.frustration.trim().length > 0;
      case 7: return formData.desiredResult.trim().length > 0;
      case 8: return formData.email.trim().length > 0 && formData.phone.trim().length > 0;
      default: return true;
    }
  };

  const renderStep = () => {
    const inputClasses = "w-full bg-transparent border-b-2 border-[#f8f6e2]/20 focus:border-[#ff00c7] outline-none py-4 text-xl md:text-2xl text-[#f8f6e2] placeholder-[#f8f6e2]/30 transition-colors duration-300";
    
    switch (currentStep) {
      case 0:
        return (
          <motion.div key="step-0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <h2 className="font-['Syne'] text-2xl md:text-4xl font-bold text-[#f8f6e2] mb-8">{t.form.steps.name}</h2>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="João Silva"
              className={inputClasses}
              autoFocus
            />
          </motion.div>
        );
      case 1:
        return (
          <motion.div key="step-1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <h2 className="font-['Syne'] text-2xl md:text-4xl font-bold text-[#f8f6e2] mb-8">{t.form.steps.business}</h2>
            <input
              type="text"
              value={formData.business}
              onChange={(e) => handleInputChange('business', e.target.value)}
              placeholder="Loom Studio"
              className={inputClasses}
              autoFocus
            />
          </motion.div>
        );
      case 2:
        return (
          <motion.div key="step-2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <h2 className="font-['Syne'] text-2xl md:text-4xl font-bold text-[#f8f6e2] mb-8">{t.form.steps.social}</h2>
            <input
              type="text"
              value={formData.social}
              onChange={(e) => handleInputChange('social', e.target.value)}
              placeholder="@instagram ou www.example.com"
              className={inputClasses}
              autoFocus
            />
          </motion.div>
        );
      case 3:
        return (
          <motion.div key="step-3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <h2 className="font-['Syne'] text-2xl md:text-4xl font-bold text-[#f8f6e2] mb-8">{t.form.steps.location}</h2>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="Tavira, Portugal"
              className={inputClasses}
              autoFocus
            />
          </motion.div>
        );
      case 4:
        return (
          <motion.div key="step-4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <h2 className="font-['Syne'] text-2xl md:text-4xl font-bold text-[#f8f6e2] mb-8">{t.form.steps.help}</h2>
            <div className="space-y-3">
              {t.form.helpOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleMultiSelect(option)}
                  className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all duration-300 ${
                    formData.helpNeeded.includes(option)
                      ? 'border-[#ff00c7] bg-[#ff00c7]/10 text-[#f8f6e2]'
                      : 'border-[#f8f6e2]/20 hover:border-[#f8f6e2]/40 text-[#f8f6e2]/70'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                      formData.helpNeeded.includes(option) ? 'border-[#ff00c7] bg-[#ff00c7]' : 'border-[#f8f6e2]/30'
                    }`}>
                      {formData.helpNeeded.includes(option) && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {option}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        );
      case 5:
        return (
          <motion.div key="step-5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <h2 className="font-['Syne'] text-2xl md:text-4xl font-bold text-[#f8f6e2] mb-8">{t.form.steps.budget}</h2>
            <div className="space-y-3">
              {t.form.budgetOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleInputChange('budget', option)}
                  className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all duration-300 ${
                    formData.budget === option
                      ? 'border-[#ff00c7] bg-[#ff00c7]/10 text-[#f8f6e2]'
                      : 'border-[#f8f6e2]/20 hover:border-[#f8f6e2]/40 text-[#f8f6e2]/70'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      formData.budget === option ? 'border-[#ff00c7] bg-[#ff00c7]' : 'border-[#f8f6e2]/30'
                    }`}>
                      {formData.budget === option && <span className="w-2 h-2 bg-white rounded-full" />}
                    </span>
                    {option}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        );
      case 6:
        return (
          <motion.div key="step-6" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <h2 className="font-['Syne'] text-2xl md:text-4xl font-bold text-[#f8f6e2] mb-8">{t.form.steps.frustration}</h2>
            <textarea
              value={formData.frustration}
              onChange={(e) => handleInputChange('frustration', e.target.value)}
              placeholder="..."
              rows={3}
              className={`${inputClasses} resize-none border-2 rounded-xl px-4`}
              autoFocus
            />
          </motion.div>
        );
      case 7:
        return (
          <motion.div key="step-7" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <h2 className="font-['Syne'] text-2xl md:text-4xl font-bold text-[#f8f6e2] mb-8">{t.form.steps.result}</h2>
            <textarea
              value={formData.desiredResult}
              onChange={(e) => handleInputChange('desiredResult', e.target.value)}
              placeholder="..."
              rows={3}
              className={`${inputClasses} resize-none border-2 rounded-xl px-4`}
              autoFocus
            />
          </motion.div>
        );
      case 8:
        return (
          <motion.div key="step-8" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <h2 className="font-['Syne'] text-2xl md:text-4xl font-bold text-[#f8f6e2] mb-8">{t.form.steps.contact}</h2>
            <div className="space-y-6">
              <div>
                <label className="text-sm text-[#f8f6e2]/60 mb-2 block">{t.form.emailLabel}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="email@exemplo.com"
                  className={inputClasses}
                  autoFocus
                />
              </div>
              <div>
                <label className="text-sm text-[#f8f6e2]/60 mb-2 block">{t.form.phoneLabel}</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+351 912 345 678"
                  className={inputClasses}
                />
              </div>
            </div>
          </motion.div>
        );
      default:
        return null;
    }
  };

  if (isComplete) {
    return (
      <div className="min-h-screen bg-[#242328] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-lg"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 rounded-full bg-[#ff00c7] flex items-center justify-center mx-auto mb-8"
          >
            <Check className="w-10 h-10 text-white" />
          </motion.div>
          <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold text-[#f8f6e2] mb-6">
            {t.form.thankYou.title}
          </h2>
          <p className="text-[#f8f6e2]/70 text-lg mb-10 leading-relaxed">
            {t.form.thankYou.message}
          </p>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-3 bg-[#ff00c7] text-[#f8f6e2] px-8 py-4 rounded-full font-semibold hover:bg-[#ff00c7]/90 transition-colors"
          >
            {t.form.thankYou.cta}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#242328] relative overflow-hidden">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#ff00c7]/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#ff00c7]/3 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#242328]/80 backdrop-blur-md border-b border-[#f8f6e2]/5">
        <div className="max-w-4xl mx-auto px-6">
          <nav className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-2 group">
              <ArrowLeft className="w-5 h-5 text-[#f8f6e2]/60 group-hover:text-[#ff00c7] transition-colors" />
              <img src="/logo-loom-white.png" alt="Loom Studio" className="h-14 md:h-16 w-auto" />
            </Link>
            <LanguageSwitcher variant="dark" />
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-32 pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Hero */}
          {currentStep === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <h1 className="font-['Syne'] text-4xl md:text-5xl font-bold text-[#f8f6e2] mb-4">
                {t.form.title}
              </h1>
              <p className="text-[#f8f6e2]/60 text-lg mb-4">
                {t.form.subtitle}
              </p>
              <span className="inline-flex items-center gap-2 text-[#ff00c7] text-sm">
                <Clock className="w-4 h-4" />
                {t.form.timeNote}
              </span>
            </motion.div>
          )}

          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex justify-between text-sm text-[#f8f6e2]/40 mb-2">
              <span>{currentStep + 1}/{totalSteps}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1 bg-[#f8f6e2]/10 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-[#ff00c7]"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Form Step */}
          <AnimatePresence mode="wait">
            {renderStep()}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between mt-12">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
                currentStep === 0
                  ? 'opacity-0 pointer-events-none'
                  : 'text-[#f8f6e2]/60 hover:text-[#f8f6e2]'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
              {t.form.back}
            </button>

            {currentStep < totalSteps - 1 ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition-all ${
                  canProceed()
                    ? 'bg-[#ff00c7] text-[#f8f6e2] hover:bg-[#ff00c7]/90'
                    : 'bg-[#f8f6e2]/10 text-[#f8f6e2]/30 cursor-not-allowed'
                }`}
              >
                {t.form.next}
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canProceed() || isSubmitting}
                className={`flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition-all ${
                  canProceed() && !isSubmitting
                    ? 'bg-[#ff00c7] text-[#f8f6e2] hover:bg-[#ff00c7]/90'
                    : 'bg-[#f8f6e2]/10 text-[#f8f6e2]/30 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? 'A enviar...' : t.form.submit}
                {!isSubmitting && <ArrowRight className="w-5 h-5" />}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
